import { useEffect, useMemo, useRef, useState } from "react";
import type { ApiStock } from "../services/api";
import { useLivePrice } from "../hooks/useLivePrice";
import { fmtNum, fmtPercent, percentClass } from "../services/format";

interface Props {
  open: boolean;
  stock: ApiStock | null;
  onClose: () => void;
  onConfirm: (price: number, quantity: number) => Promise<void> | void;
}

const QUICK_RATIOS = [
  { label: "全仓", ratio: 1 },
  { label: "半仓", ratio: 0.5 },
  { label: "1/3仓", ratio: 1 / 3 },
  { label: "1/4仓", ratio: 0.25 },
];

// 减仓对话框：计算减仓后的新成本价和剩余仓位，提供快捷比例选择
export function ReducePositionDialog({ open, stock, onClose, onConfirm }: Props) {
  const [sellPrice, setSellPrice] = useState("");
  const [quantity, setQuantity] = useState("");
  const [busy, setBusy] = useState(false);
  const manualEdited = useRef(false);
  const live = useLivePrice(stock ? `${stock.exchange}${stock.code}` : undefined, open && !!stock);

  // 打开对话框时重置手动编辑标记
  useEffect(() => {
    if (open) manualEdited.current = false;
  }, [open]);

  // 未手动编辑时持续跟随当日最新价自动填入
  useEffect(() => {
    if (open && stock && !manualEdited.current && live.price != null) {
      setSellPrice(String(live.price));
    }
  }, [open, stock, live.price]);

  const currentQty = stock?.quantity ?? 0;
  const currentCost = stock?.costPrice ?? 0;

  const result = useMemo(() => {
    const p = Number(sellPrice);
    const q = Number(quantity);
    if (!p || p <= 0 || !q || q <= 0 || q > currentQty) return null;
    const remainQty = currentQty - q;
    const newCost =
      remainQty <= 0
        ? 0
        : Math.round(((currentQty * currentCost - q * p) / remainQty) * 100) / 100;
    return { remainQty, newCost };
  }, [sellPrice, quantity, currentQty, currentCost]);

  if (!open || !stock) return null;

  const pickRatio = (ratio: number) => {
    setQuantity(String(Math.floor(currentQty * ratio)));
  };

  const submit = async () => {
    const p = Number(sellPrice);
    const q = Number(quantity);
    if (!p || p <= 0 || !q || q <= 0 || q > currentQty) return;
    setBusy(true);
    try {
      await onConfirm(p, q);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dialog-overlay" onMouseDown={onClose}>
      <div className="dialog" onMouseDown={(e) => e.stopPropagation()}>
        <div className="dialog-title">
          {stock.name}（{stock.exchange}
          {stock.code}）
        </div>

        <div className="flex justify-between mb-3 text-13px">
          <span className="flat">
            当前持仓 {fmtNum(currentQty)} 股 / 成本 {fmtNum(currentCost)}
          </span>
        </div>
        <div className="flex justify-between mb-4 text-13px">
          <span className="flat">当日价格</span>
          <span>
            <span className="font-semibold">{fmtNum(live.price)}</span>{" "}
            <span className={percentClass(live.percent)}>{fmtPercent(live.percent == null ? null : live.percent)}</span>
          </span>
        </div>

        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <label className="label" style={{ marginBottom: 0 }}>卖出价（元）</label>
            {manualEdited.current && live.price != null && Number(sellPrice) !== live.price && (
              <button
                type="button"
                className="text-12px"
                style={{ color: "var(--accent)" }}
                onClick={() => {
                  manualEdited.current = false;
                  setSellPrice(String(live.price));
                }}
              >
                ↺ 恢复最新价
              </button>
            )}
          </div>
          <input
            className="input"
            type="number"
            min={0.01}
            step="0.01"
            inputMode="decimal"
            value={sellPrice}
            onChange={(e) => {
              manualEdited.current = true;
              setSellPrice(e.target.value);
            }}
          />
        </div>
        <div className="mb-3">
          <label className="label">减仓数量（股）</label>
          <input
            className="input"
            type="number"
            min={1}
            max={currentQty}
            inputMode="numeric"
            placeholder="请输入减仓数量"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
          <div className="flex gap-2 mt-2">
            {QUICK_RATIOS.map((r) => (
              <button
                key={r.label}
                className="btn btn-sm flex-1"
                onClick={() => pickRatio(r.ratio)}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {result && (
          <div className="mb-4 p-3 rounded-lg" style={{ background: "var(--hover)" }}>
            <div className="flex justify-between text-13px">
              <span className="flat">剩余仓位</span>
              <span className="font-semibold">{result.remainQty} 股</span>
            </div>
            <div className="flex justify-between text-13px mt-1">
              <span className="flat">减仓后成本价</span>
              <span className="font-semibold">{fmtNum(result.newCost)} 元</span>
            </div>
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button className="btn" onClick={onClose}>
            取消
          </button>
          <button className="btn btn-danger" disabled={busy || !result} onClick={submit}>
            确认减仓
          </button>
        </div>
      </div>
    </div>
  );
}
